// src/features/mocktests/components/SpeakingPage.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, CircularProgress, Typography } from "@mui/material";
import {
  useLazyGetPracticeTestDataQuery,
  useLazyGetTestAttemptResultsQuery,
  useUpdateTestAttemptMutation,
} from "../mockTestSlice";
import { useLazyGetBundleTestDataQuery } from "../../bundles/bundleSlice";
import SpeakingSection from "./SpeakingSection";
import TestResultsPopup from "./TestResultsPopup";
import {
  InstructionWrapper,
  useInstructions,
} from "../../../components/Instructions";
import { useTestNavigation } from "../utils/testNavigation";

export default function SpeakingPage({ isBundle = false }) {
  const { testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Get navigation utilities for test type
  const { baseRoute, backRoute, getTestUrl, testType } = useTestNavigation();

  // --- Instruction system integration ---
  const {
    instructionState,
    showInstructionModal,
    hideInstructionModal,
    showConfirmationDialog,
    hideConfirmationDialog,
    showUnattemptedDialog,
    hideUnattemptedDialog,
    showTimeExpiryDialog,
    hideTimeExpiryDialog,
  } = useInstructions();

  // Show Speaking instructions at section start
  const [showInitialInstructions, setShowInitialInstructions] = useState(true);

  const [allSpeakingAudios, setAllSpeakingAudios] = useState([]);
  const [speakingTestCompleted, setSpeakingTestCompleted] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [showResultsPopup, setShowResultsPopup] = useState(false);

  const [currentPart, setCurrentPart] = useState(1);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [speakingCardIds, setSpeakingCardIds] = useState([]);
  const [localSpeakingData, setLocalSpeakingData] = useState(null);

  const studentAttemptId = location.state?.studentAttemptId;
  const speakingTestId =
    location.state?.speakingTestId ||
    location.state?.mockTest?.oetTest?.speakingId;

  // Check for essential state and redirect if missing (browser refresh)
  useEffect(() => {
    if (!studentAttemptId || !location.state) {
      console.log(
        "Essential state missing, redirecting to main mock test screen"
      );
      navigate(getTestUrl(testId), { replace: true });
    }
  }, [studentAttemptId, location.state, navigate, testId, getTestUrl]);

  // REMOVE MODAL state, hardcode isAiBased
  const isAiBased = true; // <<--- FORCE AI always!

  // Get card IDs from location
  useEffect(() => {
    if (
      Array.isArray(location.state?.mockTest?.oetTest?.speakingCardIds) &&
      location.state.mockTest.oetTest.speakingCardIds.length > 0
    ) {
      setSpeakingCardIds(location.state.mockTest.oetTest.speakingCardIds);
    } else if (
      Array.isArray(location.state?.speakingCardIds) &&
      location.state.speakingCardIds.length > 0
    ) {
      setSpeakingCardIds(location.state.speakingCardIds);
    }
  }, [location.state]);

  // Use bundle API when isBundle is true, otherwise use mock test API
  const [triggerGetMockPracticeTestData, { data: mockPracticeData, isLoading: mockLoading, error: mockError }] =
    useLazyGetPracticeTestDataQuery();
  const [triggerGetBundleTestData, { data: bundlePracticeData, isLoading: bundleLoading, error: bundleError }] =
    useLazyGetBundleTestDataQuery();
  
  // Use the appropriate data based on isBundle
  const triggerGetPracticeTestData = isBundle ? triggerGetBundleTestData : triggerGetMockPracticeTestData;
  const practiceData = isBundle ? bundlePracticeData : mockPracticeData;
  const isLoading = isBundle ? bundleLoading : mockLoading;
  const error = isBundle ? bundleError : mockError;

  const [updateTestAttempt] = useUpdateTestAttemptMutation();
  const [getTestAttemptResults] = useLazyGetTestAttemptResultsQuery();

  // Extract submission status from API response
  const isSpeakingSubmitted = 
    practiceData?.data?.isSpeakingSubmitted || 
    localSpeakingData?.data?.isSpeakingSubmitted || 
    false;

  // Handle speaking test completion and show results in simulation mode
  const handleSpeakingCompletion = async () => {
    if (!location.state?.isPracticeMode && studentAttemptId) {
      try {
        const resultsResponse = await getTestAttemptResults(
          studentAttemptId
        ).unwrap();
        setTestResults(resultsResponse);
        setShowResultsPopup(true);
      } catch (resultsError) {
        console.error("Error fetching results:", resultsError);
      }
    }
  };

  // Guard: Only fetch when all params are ready and test is not completed
  useEffect(() => {
    console.log("SpeakingPage useEffect triggered", {
      speakingTestCompleted,
      studentAttemptId,
      speakingTestId,
      speakingCardIds: speakingCardIds.length,
      currentCardIndex,
      cardId: speakingCardIds[currentCardIndex],
    });

    if (
      speakingTestCompleted ||
      !studentAttemptId ||
      !speakingTestId ||
      !Array.isArray(speakingCardIds) ||
      speakingCardIds.length === 0 ||
      !speakingCardIds[currentCardIndex]
    ) {
      console.log("SpeakingPage useEffect early return");
      return;
    }

    console.log("SpeakingPage calling API");
    setLocalSpeakingData(null);

    triggerGetPracticeTestData({
      studentAttemptId,
      speakingId: speakingTestId,
      currentSection: "speaking",
      speakingCardId: speakingCardIds[currentCardIndex],
    });
    // eslint-disable-next-line
  }, [
    studentAttemptId,
    speakingTestId,
    speakingCardIds,
    currentCardIndex,
    speakingTestCompleted,
    triggerGetPracticeTestData,
  ]);

  useEffect(() => {
    if (practiceData) {
      setLocalSpeakingData(practiceData);
    }
  }, [practiceData]);

  // Show Speaking instructions when data is loaded
  useEffect(() => {
    if (showInitialInstructions && localSpeakingData) {
      const mode = location.state?.isPracticeMode ? "practice" : "simulation";
      showInstructionModal("speaking", null, mode);
      setShowInitialInstructions(false);
    }
  }, [
    showInitialInstructions,
    localSpeakingData,
    showInstructionModal,
    location.state,
  ]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleNextPart = () => {
    setCurrentPart((prev) => prev + 1);
    setCurrentOffset((prev) => prev + 1);
    setAnswers({});
    setCurrentCardIndex((prev) => prev + 1);
  };

  const transformSpeakingData = (apiData) => {
    let speakingTest = null;
    if (apiData?.data?.data?.speakingTest) {
      speakingTest = apiData.data.data.speakingTest;
    } else if (apiData?.data?.speakingTest) {
      speakingTest = apiData.data.speakingTest;
    }
    if (!speakingTest) return null;
    return {
      speakingTest,
      currentPart,
      totalParts: speakingCardIds.length || 2,
    };
  };

  // Show loading only if not completed and no data
  if ((isLoading || !localSpeakingData) && !speakingTestCompleted) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={60} thickness={5} />
        <Typography sx={{ ml: 2 }}>Loading speaking data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography color="error">
          Error loading speaking test: {error.message}
        </Typography>
      </Box>
    );
  }

  // If test is completed, we can proceed even without localSpeakingData
  const speakingData = localSpeakingData
    ? transformSpeakingData(localSpeakingData)
    : null;

  // Only show "no data" message if we're not completed and have no data
  if (
    !speakingData &&
    !isLoading &&
    localSpeakingData &&
    !speakingTestCompleted
  ) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography>No speaking data available</Typography>
      </Box>
    );
  }

  // ====== No modal, just always pass AI ======
  return (
    <>
      {/* Instruction System Wrapper */}
      <InstructionWrapper
        instructionState={instructionState}
        onInstructionNext={hideInstructionModal}
        onInstructionClose={hideInstructionModal}
        onConfirmationConfirm={() => {
          hideConfirmationDialog();
          hideUnattemptedDialog();
        }}
        onConfirmationClose={hideConfirmationDialog}
        onUnattemptedContinue={() => {
          hideUnattemptedDialog();
        }}
        onUnattemptedClose={() => {
          hideUnattemptedDialog();
        }}
        onTimeExpiryContinue={hideTimeExpiryDialog}
        timeRemaining={null}
        unattemptedCount={0}
        nextSection="results"
      />
      <div>
        <SpeakingSection
          speakingData={speakingData}
          currentPart={currentPart}
          answers={answers}
          onAnswerChange={handleAnswerChange}
          onNextPart={handleNextPart}
          studentAttemptId={studentAttemptId}
          speakingTestId={speakingTestId}
          setLocalSpeakingData={setLocalSpeakingData}
          setCurrentPart={setCurrentPart}
          setCurrentOffset={setCurrentOffset}
          navigate={navigate}
          location={location}
          dispatch={dispatch}
          speakingCardIds={speakingCardIds}
          currentCardIndex={currentCardIndex}
          setCurrentCardIndex={setCurrentCardIndex}
          allSpeakingAudios={allSpeakingAudios}
          setAllSpeakingAudios={setAllSpeakingAudios}
          isAiBased={isAiBased} // Always true!
          isSubmitted={isSpeakingSubmitted}
          updateTestAttempt={updateTestAttempt}
          speakingAudioMethod={location.state?.speakingAudioMethod} // Pass the selected audio method
          setSpeakingTestCompleted={setSpeakingTestCompleted} // Add completion flag setter
          speakingTestCompleted={speakingTestCompleted} // Pass completion status
          onSpeakingCompletion={handleSpeakingCompletion} // Pass completion handler for simulation mode
          instrOpen={
            instructionState.showInstructions &&
            instructionState.currentSection === "speaking"
          }
        />
      </div>

      {/* Test Results Popup */}
      <TestResultsPopup
        open={showResultsPopup}
        onClose={() => setShowResultsPopup(false)}
        resultsData={testResults}
        onContinue={() => navigate(backRoute)}
        attemptId={studentAttemptId}
        mockTestId={testId}
      />
    </>
  );
}
